import { useEffect, useState } from "react";
import { Archive, MessageSquare, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useSandboxConversations } from "@/context/SandboxConversationsContext";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";

/** Sandbox thread history, laid out like Research Assistant's ConversationsList. */
export function SandboxConversationsList() {
  const {
    conversations,
    isLoading,
    loadFailed,
    activeConversationId,
    loadConversations,
    selectConversation,
    createConversation,
    archiveConversation,
  } = useSandboxConversations();
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      await createConversation();
    } catch {
      toast({
        title: "Conversation not created",
        description: "The new conversation could not be started. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleArchive = async (id: number) => {
    if (!window.confirm("Are you sure you want to archive this conversation?")) return;
    try {
      await archiveConversation(id);
    } catch {
      toast({
        title: "Conversation not archived",
        description: "The conversation is still in your list. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full max-w-[236px]">
      <div className="py-2 pl-3 pr-[10px]">
        <Button
          type="button"
          variant="outline"
          className="h-9 w-full justify-start px-3 py-0"
          onClick={() => void handleCreate()}
          disabled={isCreating || isLoading}
        >
          <Plus className="h-4 w-4 shrink-0" />
          <span className="ml-2">New conversation</span>
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2 px-2 py-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-[200px]" />
          ))}
        </div>
      ) : (
        <div className="py-2" role="list" aria-label="Sandbox conversations list">
          {conversations.map((conversation) => {
            const isActive = activeConversationId === conversation.id;
            return (
              <div key={conversation.id} role="listitem" className="flex w-full max-w-[236px] flex-row items-center">
                <Button
                  type="button"
                  variant={isActive ? "secondary" : "ghost"}
                  className={cn(
                    "h-9 min-w-0 flex-1 justify-start px-3 py-0",
                    isActive && "border-2 border-primary font-medium",
                  )}
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => selectConversation(conversation.id)}
                >
                  <MessageSquare className={cn("h-4 w-4 shrink-0", isActive && "text-primary")} aria-hidden />
                  <span className="ml-2 max-w-[180px] overflow-hidden text-ellipsis">{conversation.title}</span>
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Archive conversation"
                        onClick={() => void handleArchive(conversation.id)}
                      >
                        <Archive className="h-4 w-4" aria-hidden />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Archive conversation</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            );
          })}

          {loadFailed && (
            <div role="alert" className="px-3 text-xs text-destructive">
              <p>Conversations could not be loaded.</p>
              <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => void loadConversations()}>
                Try again
              </Button>
            </div>
          )}

          {!loadFailed && conversations.length === 0 && (
            <p className="px-3 text-xs italic text-muted-foreground">No conversations yet. Start a new one to begin.</p>
          )}
        </div>
      )}
    </div>
  );
}
